import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import WheelChart from '../components/WheelChart';
import LifeSquares from '../components/LifeSquares';
import ThemeToggle from '../components/ThemeToggle';
import { wheelCategories, coachingQuestions } from '../lib/questions';
import { ArrowLeft, Download, Loader2, Trash2, Copy, Check, Pencil, StickyNote, Save, Headphones, Mail, Phone } from 'lucide-react';
import { exportToPDF } from '../lib/pdfExport';
import { toast } from 'sonner';
import MeetingReportGenerator from './MeetingReportGenerator';
import AdminAudioStatusBadge from './AdminAudioStatusBadge';
import {
  canRequestAudioForSubmission,
  isAudioEnabledSubmission,
  isFormu2Submission,
  isLegacyAudioEnabled,
} from './adminAudioEligibility';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const isUploadedAudioStatus = (status) => status === 'recording_uploaded' || status === 'submitted_to_editor';

const hasMissingAudio = (submission) => (
  submission?.form_variant === 'formu2'
    && submission?.status !== 'finalized'
    && Number(submission?.current_step || 0) >= 9
    && !isUploadedAudioStatus(submission?.affirmation_audio?.status)
);

const getSubmissionStatusLabel = (submission) => {
  if (hasMissingAudio(submission)) return 'Audio faltante';
  return submission?.status === 'finalized' ? 'Finalizado' : 'En progreso';
};

const isTestSubmission = (submission) => submission?.is_test_submission === true;

export function AdminSubmissionView({ submissionId, token, onBack }) {
  const [submission, setSubmission] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [isGuideCopied, setIsGuideCopied] = useState(false);
  const [isGeneratingGuide, setIsGeneratingGuide] = useState(false);
  const [isRequestingAudio, setIsRequestingAudio] = useState(false);
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({ name: '', email: '', linkingEmails: '' });
  const [isEditing, setIsEditing] = useState(false);

  // Edit responses state
  const [isEditResponsesOpen, setIsEditResponsesOpen] = useState(false);
  const [editResponses, setEditResponses] = useState(null);
  const [isSavingResponses, setIsSavingResponses] = useState(false);

  // Admin notes state
  const [adminNotes, setAdminNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesLastSaved, setNotesLastSaved] = useState(null);

  const loadSubmission = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Error al cargar');
      
      const subData = await res.json();
      setSubmission(subData);
      setAdminNotes(subData.admin_notes || '');
      if (subData.admin_notes_updated_at) {
        setNotesLastSaved(new Date(subData.admin_notes_updated_at));
      }
    } catch (error) {
      toast.error('Error al cargar el formulario');
      onBack();
    } finally {
      setIsLoading(false);
    }
  }, [submissionId, token, onBack]);

  useEffect(() => {
    loadSubmission();
  }, [loadSubmission]);

  const handleExportPDF = async () => {
    const success = await exportToPDF(
      'submission-pdf-content',
      `Formulario-${submission?.user?.name?.replace(/\s+/g, '_') || 'Alumno'}-${new Date().toISOString().split('T')[0]}.pdf`
    );
    if (success) {
      toast.success('PDF descargado');
    } else {
      toast.error('Error al generar PDF');
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Error al eliminar');
      
      toast.success('Formulario eliminado');
      onBack();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleRequestAudio = useCallback(async () => {
    if (!canRequestAudioForSubmission(submission)) return;

    setIsRequestingAudio(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}/request-audio`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        let message = 'No se pudo generar el link de audio';
        try {
          const errorData = await res.json();
          message = errorData?.detail || message;
        } catch (e) {
          // ignore parse errors
        }
        throw new Error(message);
      }

      const data = await res.json();
      let copied = false;
      if (data.audio_url) {
        try {
          await navigator.clipboard.writeText(data.audio_url);
          copied = true;
        } catch (clipboardError) {
          console.warn('No se pudo copiar el link de audio:', clipboardError);
          window.prompt('Copiá el link de audio', data.audio_url);
        }
      }
      toast.success(copied ? 'Link de audio generado y copiado' : 'Link de audio generado');
      await loadSubmission();
    } catch (error) {
      toast.error(error.message || 'Error solicitando audio');
    } finally {
      setIsRequestingAudio(false);
    }
  }, [loadSubmission, submission, submissionId, token]);

  const getQuestionText = (questionId, questionAnswer = null) => {
    // First check if this is a dynamic question with saved question_text
    if (questionAnswer?.question_text) {
      return questionAnswer.question_text;
    }
    
    // Look up in static questions
    for (const section of Object.values(coachingQuestions)) {
      const question = section.questions.find(q => q.id === questionId);
      if (question) return question.text;
    }
    
    // Handle dynamic wheel questions (legacy support)
    if (questionId === 'wheel_lowest_3') {
      return 'Pregunta sobre las 3 áreas más bajas de la rueda';
    }
    if (questionId.startsWith('wheel_low_')) {
      return `Pregunta sobre área baja de la rueda`;
    }
    
    return questionId;
  };

  const getLeadPhone = useCallback(() => {
    const lookup = submission?.lead_lookup || {};
    return lookup.phone || lookup.masked_phone || submission?.user?.phone || '';
  }, [submission]);

  const getLeadPhoneDigits = useCallback(() => {
    const lookup = submission?.lead_lookup || {};
    const rawPhone = lookup.phone_digits || lookup.phone || submission?.user?.phone || '';
    return String(rawPhone).replace(/\D/g, '');
  }, [submission]);

  const getLeadWhatsappUrl = useCallback(() => {
    const digits = getLeadPhoneDigits();
    return digits.length >= 8 ? `https://wa.me/${digits}` : '';
  }, [getLeadPhoneDigits]);

  const formatAnswerValue = (answer) => {
    if (answer === undefined || answer === null || answer === '') return '-';
    if (Array.isArray(answer)) {
      const validValues = answer.filter(value => value !== undefined && value !== null && `${value}`.trim() !== '');
      return validValues.length > 0 ? validValues.join(', ') : '-';
    }
    if (typeof answer === 'object') {
      try {
        return JSON.stringify(answer, null, 2);
      } catch (error) {
        return String(answer);
      }
    }
    return String(answer);
  };

  const formatWheelScore = (score) => {
    const numericScore = Number(score);
    return Number.isFinite(numericScore) ? numericScore.toFixed(1) : '-';
  };

  const generateMeetingPrepPrompt = () => {
    if (!submission || submission.status !== 'finalized') return '';

    const getAnswerById = (questionId) =>
      submission.question_answers?.find((qa) => qa.question_id === questionId)?.answer;

    const questionnaireDate = new Date(
      submission.finalized_at || submission.created_at
    ).toLocaleDateString('es-AR');

    const lowestAreas = wheelCategories
      .map((category) => ({
        name: category.name,
        score: Number(submission.wheel_data?.[category.id]?.score ?? 0),
      }))
      .sort((a, b) => a.score - b.score)
      .slice(0, 3)
      .map((area) => `- ${area.name}: ${formatWheelScore(area.score)}`)
      .join('\n');

    const objectives3m = (submission.objectives?.['3m'] || [])
      .filter(obj => obj?.description && obj.description.trim());

    const objectivesText = objectives3m.length > 0
      ? objectives3m.map((obj, index) => `${index + 1}. ${obj.description.trim()}`).join('\n')
      : '-';

    const actions3m = objectives3m.flatMap((obj) =>
      (obj.actions || [])
        .map(action => `${action || ''}`.trim())
        .filter(Boolean)
    );

    const actionsText = actions3m.length > 0
      ? actions3m.map(action => `- ${action}`).join('\n')
      : '-';

    const quickFacts = [
      `- Ubicacion: No informada en el formulario`,
      `- Dedicacion actual: ${formatAnswerValue(getAnswerById('a1'))}`,
      `- Vive con: ${formatAnswerValue(getAnswerById('b1'))}`,
      `- Situacion de pareja: ${formatAnswerValue(getAnswerById('b2'))}`,
      `- Estado de la relacion: ${formatAnswerValue(getAnswerById('b3'))}`,
      `- Hijos: ${formatAnswerValue(getAnswerById('b4'))}`,
      `- Detalle de hijos: ${formatAnswerValue(getAnswerById('b5'))}`,
      `- Horarios de sueno: ${formatAnswerValue(getAnswerById('a3'))}`,
      `- Tiempo libre real: ${formatAnswerValue(getAnswerById('a4'))}`,
      `- Calidad de sueno: ${formatAnswerValue(getAnswerById('d1'))}`,
      `- Cafe / estimulantes: ${formatAnswerValue(getAnswerById('d2'))}`,
      `- Ejercicio actual: ${formatAnswerValue(getAnswerById('d3'))}`,
      `- Parte del dia en caos: ${formatAnswerValue(getAnswerById('f1'))}`,
      `- Tres tareas semanales que repite: ${formatAnswerValue(getAnswerById('f3'))}`,
    ].join('\n');

    const questionnaireAnswers = (submission.question_answers || [])
      .filter((qa) => {
        const answer = qa?.answer;
        if (answer === undefined || answer === null || answer === '') return false;
        if (Array.isArray(answer) && answer.every(value => !`${value || ''}`.trim())) return false;
        return true;
      })
      .map((qa) => {
        const questionText = getQuestionText(qa.question_id, qa);
        return `${questionText}\n${formatAnswerValue(qa.answer)}`;
      })
      .join('\n\n');

    const wheelLines = [
      ['salud', 'Salud'],
      ['finanzas', 'Finanzas'],
      ['familiar', 'Familiar'],
      ['profesional', 'Profesional'],
      ['social', 'Social'],
      ['recreacion', 'Recreacion'],
      ['espiritualidad', 'Espiritualidad'],
      ['desarrollo_personal', 'Desarrollo Personal'],
      ['pareja', 'Pareja'],
    ]
      .map(([categoryId, label]) => `- ${label}: ${formatWheelScore(submission.wheel_data?.[categoryId]?.score)}`)
      .join('\n');

    return `Eres el asistente de preparacion de reuniones de Dario, director de Academia Cortex. Tu trabajo es analizar los datos del Planificador de Vida de un estudiante y generar una GUIA DE DEVOLUCION EN VIVO que Dario va a usar durante la videollamada con el estudiante.

IMPORTANTE: Esto NO es un informe para el estudiante. Es una guia para que DARIO lea durante la reunion. Debe estar escrita como notas de conversacion, con frases que Dario puede decir textualmente.

## DATOS DEL ESTUDIANTE A ANALIZAR

NOMBRE: ${submission.user?.name || '-'}
EMAIL: ${submission.user?.email || '-'}
EDAD: ${submission.life_squares?.currentAge || '-'}
FECHA CUESTIONARIO: ${questionnaireDate}

DATOS RAPIDOS:
${quickFacts}

RUEDA DE LA VIDA:
${wheelLines}

AREAS MAS BAJAS DETECTADAS:
${lowestAreas || '-'}

OBJETIVOS 3 MESES:
${objectivesText}

ACCIONES 3 MESES:
${actionsText}

RESPUESTAS AL CUESTIONARIO:
${questionnaireAnswers || '-'}

## ESTILO DE DARIO EN REUNIONES

- Habla directo, sin rodeos, pero con empatia
- Tutea al estudiante (vos, tenes, haces)
- Usa analogias practicas: "comerse el sapo", "el general que ordena sus filas", "paralisis por analisis", el ejemplo de la ropa del ropero (80/20)
- No endulza la realidad pero siempre con respeto
- Hace pausas para preguntar "hasta ahi vamos bien?" o "se entiende?"
- Conecta lo que el estudiante escribio con lo que realmente esta pasando
- Identifica contradicciones entre la Rueda de la Vida y las respuestas abiertas
- Siempre cierra con acciones concretas para los proximos 7 dias

## METODOLOGIA DE ANALISIS

1. Cruza los puntajes de la Rueda de la Vida con las respuestas abiertas
2. Busca CONTRADICCIONES (ej: puntaje alto en pareja pero menciona tension)
3. Identifica CIRCULOS VICIOSOS (mal sueno -> mala alimentacion -> baja energia -> malas decisiones)
4. Detecta PATRONES de dispersion, sabotaje, postergacion
5. Prioriza por IMPACTO: que cambio pequeno genera el mayor efecto cascada
6. Identifica que es lo que el estudiante YA SABE pero NO HACE

## PILARES QUE SIEMPRE EVALUAS (incluir los que apliquen)

### Sueno
- Objetivo: 7-8 horas minimo
- Acostarse antes de las 22:30-23:00
- El mal sueno sube cortisol, genera antojos de carbohidratos, baja la capacidad de decision
- Mencionar pulseras de monitoreo (Whoop, GoodLife ~400 USD) si tiene presupuesto
- El sueno es REGLA NUMERO UNO, sin sueno nada funciona

### Alimentacion
- Tendencia cetogenica: no mezclar proteinas con carbohidratos
- El mecanismo: intestino inflamado -> intestino permeable -> particulas en sangre -> cruzan barrera hematoencefalica -> neblina mental
- El circulo vicioso: carbohidratos -> pico glucosa -> insulina -> baja glucosa -> antojos -> mas carbohidratos -> despierto hasta tarde -> mal sueno -> dia siguiente peor
- Protocolo gradual semana a semana
- Indicador de que vas bien: ir al bano todos los dias y que la deposicion salga perfecta

### Gestion del Tiempo
- 16 horas utiles por dia, las primeras 3 de la manana son las mejores cognitivamente
- Despues del mediodia baja el rendimiento
- Principio de Pareto: 80/20 en actividades, clientes, compromisos
- "Comerse el sapo": arrancar con lo mas feo/importante
- Aprender a decir NO
- Mover ejercicio/yoga/meditacion hacia el mediodia, dejar la manana para decisiones

### Sistematizacion
- Estructurar el dia como sistema, no improvisar
- Si no cumples con 3 tareas diarias, baja a 2
- Delegar con instructivos de 10 minutos
- KPIs personales
- "Si tu ropero esta desordenado, tu habitacion desordenada, tu ambiente de trabajo desordenado, eso es un reflejo de tu estado interior"

### Gestion Emocional
- La ansiedad consume energia que no se contabiliza
- La comida como mecanismo para tapar ansiedad
- La dispersion genera frustracion interna
- El audio de respiracion ayuda a bajar la ansiedad

### Ejercicio
- No va a haber forma de hacerlo con ganas, hay que hacerlo SIN GANAS
- A partir de los 40 cuidar la perdida de musculo
- Crear musculo es de las cosas mas complejas del organismo
- Caldo de huesos, creatina, glicina para sostener masa muscular
- El organismo siempre intenta gastar lo minimo de energia

### Audio de Respiracion
- Patrones de respiracion diafragmatica + apnea con audio
- Hacerlo POR LA MANANA es mucho mas efectivo que por la noche
- Despues del audio: 2+ horas de claridad mental
- Puede usar una ronda durante el reposo de la tarde tambien

### Inteligencia Artificial
- Si no usa IA: recomendar suscripcion ChatGPT o Claude
- ChatGPT: boton de microfono, hablar en bloques de 3 min
- Siempre elegir modelo "thinking" (no automatico)
- Elimina el sindrome de la hoja en blanco
- Claude en Chrome: ve la pantalla, guia paso a paso en cualquier app
- 1 hora de charla diaria con IA = encontrar procesos que funcionan 15-20% mejor

### Pareja/Familia
- Contradicciones entre puntaje de rueda y respuestas abiertas
- Espacio semanal de 30 min para conexion (no para resolver problemas)
- Familia ensamblada: definir roles con claridad
- "Lo que haces por amor, el otro no lo entiende hasta los 35 cuando tiene sus propios hijos"

FORMATO DE RESPUESTA

Escribi la guia en texto corrido, sin usar markdown, sin asteriscos, sin simbolos ##, sin tablas, sin negritas. Usa texto plano limpio. Separa las secciones con una linea en blanco y un titulo simple en mayusculas. El objetivo es que Dario pueda leerlo como si fueran sus propias notas.

Estructura:

GUIA DE DEVOLUCION - [Nombre del Estudiante]

DATOS CLAVE
Edad, ubicacion, dedicacion, areas mas bajas de la rueda con puntajes, horarios de sueno.

APERTURA (2 min)
Una frase para abrir la conversacion, romper el hielo, preguntar como esta.

Luego analiza entre 4 y 7 patrones. Para cada patron escribi:

PATRON [numero]: [titulo descriptivo]
Lo que escribio: [cita textual corta del formulario]
Lo que realmente pasa: [tu analisis en 2-3 lineas]
Frase para decirle: "[Frase textual que Dario puede decir en la reunion, entre comillas]"
Pregunta para hacerle: [pregunta para profundizar o confirmar]
Accion concreta: [que tiene que hacer esta semana]

CIERRE Y PLAN DE 7 DIAS
Lista las acciones ordenadas por impacto, numeradas del 1 al 5 maximo, con una breve razon al lado.

Frase de cierre: "[Frase motivacional personalizada para cerrar, entre comillas]"

RECORDATORIOS OPERATIVOS
Lista simple de recordatorios: agendar proxima sesion con Marian, modulos pendientes, audio de respiracion, y otros especificos del caso.

REGLAS QUE DEBES SEGUIR

- Cada patron tiene que tener una frase textual que Dario pueda leer tal cual en la reunion.
- Las frases deben sonar naturales, como si las dijera en una conversacion.
- Detecta entre 4 y 7 patrones.
- Incluye preguntas para hacerle al estudiante, la reunion es una conversacion, no un monologo.
- Las acciones concretas son para ESTA SEMANA, no para algun dia.
- Si el estudiante puso "Todo" en area de caos, mencionarlo, es una senal fuerte.
- Si hay sustancias (alcohol, cannabis, microdosis), abordarlo con respeto y sin juicio.
- Si el sueno es menor a 6 horas, es PRIORIDAD ABSOLUTA antes que cualquier otra cosa.
- Conecta los patrones entre si, muestra como uno alimenta al otro.
- El tono es de coaching directo, no de terapeuta ni de guru.
- NO uses markdown, asteriscos, negritas, tablas ni simbolos especiales. Solo texto plano.`;
  };

  // Genera todo el contenido como texto plano para copiar
  const generateTextContent = () => {
    if (!submission) return '';
    
    let text = '';
    
    // Header
    text += `=== FORMULARIO DE ${submission.user?.name?.toUpperCase() || 'ALUMNO'} ===\n`;
    text += `Email: ${submission.user?.email || 'N/A'}\n`;
    const leadPhone = getLeadPhone();
    if (leadPhone) {
      text += `Telefono: ${leadPhone}\n`;
    }
    text += `Fecha: ${new Date(submission.created_at).toLocaleDateString('es-AR')}\n`;
    if (submission.finalized_at) {
      text += `Finalizado: ${new Date(submission.finalized_at).toLocaleDateString('es-AR')}\n`;
    }
    text += `Estado: ${getSubmissionStatusLabel(submission)}\n`;
    text += '\n';

    // Cuadraditos de Vida
    if (submission.life_squares?.currentAge && submission.life_squares?.targetAge) {
      text += `--- CUADRADITOS DE VIDA ---\n`;
      text += `Edad actual: ${submission.life_squares.currentAge} años\n`;
      text += `Expectativa de vida: ${submission.life_squares.targetAge} años\n`;
      const yearsLived = parseInt(submission.life_squares.currentAge);
      const yearsRemaining = parseInt(submission.life_squares.targetAge) - yearsLived;
      text += `Años vividos: ${yearsLived} | Años por vivir: ${yearsRemaining}\n`;
      text += '\n';
    }

    // Rueda de la Vida
    if (submission.wheel_data && Object.keys(submission.wheel_data).length > 0) {
      text += `--- RUEDA DE LA VIDA ---\n`;
      wheelCategories.forEach(cat => {
        const score = submission.wheel_data[cat.id]?.score || 0;
        const bar = '█'.repeat(Math.round(score)) + '░'.repeat(10 - Math.round(score));
        text += `${cat.name}: ${score.toFixed(1)}/10 ${bar}\n`;
      });
      text += '\n';
    }

    // Objetivos
    if (submission.objectives) {
      text += `--- OBJETIVOS ---\n`;
      ['3m', '6m', '9m'].forEach(period => {
        const periodObjectives = submission.objectives[period] || [];
        const title = period === '3m' ? '3 MESES' : period === '6m' ? '6 MESES' : '9 MESES';
        const validObjectives = periodObjectives.filter(obj => obj.description);
        
        if (validObjectives.length > 0) {
          text += `\n[${title}]\n`;
          validObjectives.forEach((obj, idx) => {
            text += `${idx + 1}. ${obj.description}\n`;
            if (obj.dailyTime) text += `   Tiempo diario: ${obj.dailyTime}\n`;
            if (obj.actions?.filter(a => a).length > 0) {
              text += `   Acciones:\n`;
              obj.actions.filter(a => a).forEach((action, i) => {
                text += `     - ${action}\n`;
              });
            }
          });
        }
      });
      text += '\n';
    }

    // Respuestas a preguntas
    if (submission.question_answers && submission.question_answers.length > 0) {
      text += `--- RESPUESTAS A PREGUNTAS ---\n\n`;
      submission.question_answers.forEach(qa => {
        const answer = qa.answer;
        if (!answer || (Array.isArray(answer) && answer.length === 0)) return;
        const questionText = getQuestionText(qa.question_id, qa);
        const displayAnswer = Array.isArray(answer) ? answer.join(', ') : answer;
        text += `P: ${questionText}\n`;
        text += `R: ${displayAnswer}\n\n`;
      });
    }

    // Resumen IA
    if (submission.ai_summary) {
      text += `--- RESUMEN Y RECOMENDACIONES IA ---\n`;
      text += `${submission.ai_summary}\n`;
    }

    return text;
  };

  const handleCopyText = async () => {
    const text = generateTextContent();
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      toast.success('Contenido copiado al portapapeles');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const handleCopyLiveGuide = async () => {
    const text = submission?.meeting_guide;
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setIsGuideCopied(true);
      toast.success('Guia copiada al portapapeles');
      setTimeout(() => setIsGuideCopied(false), 2000);
    } catch (error) {
      toast.error('Error al copiar la guia');
    }
  };

  const handleGenerateMeetingGuide = async () => {
    const prompt = generateMeetingPrepPrompt();
    if (!prompt) {
      toast.error('La guia solo se puede generar con el formulario finalizado');
      return;
    }

    setIsGeneratingGuide(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/ai/meeting-guide`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          submission_id: submissionId,
          prompt,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || 'Error al generar la guia');
      }

      setSubmission((prev) => ({
        ...prev,
        meeting_guide: data.guide,
        meeting_guide_generated_at: data.generated_at,
      }));
      toast.success('Guia de devolucion generada');
    } catch (error) {
      toast.error(error.message || 'Error al generar la guia');
    } finally {
      setIsGeneratingGuide(false);
    }
  };

  // Open edit modal
  const handleOpenEdit = () => {
    setEditData({
      name: submission.user?.name || '',
      email: submission.user?.email || '',
      linkingEmails: (submission.linking_emails || []).join(', ')
    });
    setIsEditModalOpen(true);
  };

  // Save edits
  const handleSaveEdit = async () => {
    if (!editData.name.trim() || !editData.email.trim()) {
      toast.error('Nombre y email son obligatorios');
      return;
    }

    const linkingEmails = editData.linkingEmails
      .split(/[\s,;]+/)
      .map(email => email.trim().toLowerCase())
      .filter(Boolean);
    const invalidLinkingEmail = linkingEmails.find(
      email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    );
    if (invalidLinkingEmail) {
      toast.error(`Revisá el email alternativo: ${invalidLinkingEmail}`);
      return;
    }

    setIsEditing(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user: {
            name: editData.name.trim(),
            email: editData.email.trim()
          },
          linking_emails: [...new Set(linkingEmails)]
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Error al guardar');
      }
      
      const result = await res.json();
      setSubmission(result.submission);
      setIsEditModalOpen(false);
      toast.success('Datos actualizados correctamente');
    } catch (error) {
      toast.error(error.message || 'Error al guardar cambios');
    } finally {
      setIsEditing(false);
    }
  };


  // === EDIT RESPONSES ===
  const handleOpenEditResponses = () => {
    setEditResponses({
      question_answers: JSON.parse(JSON.stringify(submission.question_answers || [])),
      wheel_data: JSON.parse(JSON.stringify(submission.wheel_data || {})),
      life_squares: JSON.parse(JSON.stringify(submission.life_squares || {})),
      objectives: JSON.parse(JSON.stringify(submission.objectives || {})),
    });
    setIsEditResponsesOpen(true);
  };

  const handleSaveResponses = async () => {
    if (!editResponses) return;
    setIsSavingResponses(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editResponses)
      });
      if (!res.ok) throw new Error('Error al guardar');
      const result = await res.json();
      setSubmission(result.submission);
      setIsEditResponsesOpen(false);
      toast.success('Respuestas actualizadas correctamente');
    } catch (error) {
      toast.error(error.message || 'Error al guardar respuestas');
    } finally {
      setIsSavingResponses(false);
    }
  };

  const updateQuestionAnswer = (index, newAnswer) => {
    setEditResponses(prev => {
      const updated = { ...prev };
      updated.question_answers = [...prev.question_answers];
      updated.question_answers[index] = { ...updated.question_answers[index], answer: newAnswer };
      return updated;
    });
  };

  const updateWheelScore = (categoryId, newScore) => {
    setEditResponses(prev => ({
      ...prev,
      wheel_data: {
        ...prev.wheel_data,
        [categoryId]: { ...prev.wheel_data[categoryId], score: parseFloat(newScore) || 0 }
      }
    }));
  };

  const updateLifeSquares = (field, value) => {
    setEditResponses(prev => ({
      ...prev,
      life_squares: { ...prev.life_squares, [field]: value }
    }));
  };

  const updateObjective = (period, index, field, value) => {
    setEditResponses(prev => {
      const updated = { ...prev };
      updated.objectives = { ...prev.objectives };
      updated.objectives[period] = [...(prev.objectives[period] || [])];
      updated.objectives[period][index] = { ...updated.objectives[period][index], [field]: value };
      return updated;
    });
  };

  // === ADMIN NOTES ===
  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ admin_notes: adminNotes })
      });
      if (!res.ok) throw new Error('Error al guardar notas');
      setNotesLastSaved(new Date());
      toast.success('Notas guardadas');
    } catch (error) {
      toast.error('Error al guardar notas');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const getChartData = () => {
    if (!submission?.wheel_data) return { labels: [], values: [] };
    const labels = wheelCategories.map(c => c.name);
    const values = wheelCategories.map(c => submission.wheel_data[c.id]?.score || 0);
    return { labels, values };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!submission) return null;

  const { labels, values } = getChartData();
  const hasMeetingGuide = Boolean(submission.meeting_guide?.trim());
  const leadPhone = getLeadPhone();
  const leadWhatsappUrl = getLeadWhatsappUrl();

  return (
    <div className="min-h-screen bg-background">
      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Editar Datos del Alumno</DialogTitle>
            <DialogDescription>
              Modifica los datos básicos del alumno. Los cambios se guardarán inmediatamente.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Nombre</Label>
              <Input
                id="edit-name"
                value={editData.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nombre completo"
                data-testid="edit-submission-name"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editData.email}
                onChange={(e) => setEditData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="correo@ejemplo.com"
                data-testid="edit-submission-email"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-linking-emails">Emails alternativos para Seguimiento</Label>
              <Input
                id="edit-linking-emails"
                value={editData.linkingEmails}
                onChange={(e) => setEditData(prev => ({ ...prev, linkingEmails: e.target.value }))}
                placeholder="email.usado.en.seguimiento@ejemplo.com"
                data-testid="edit-submission-linking-emails"
              />
              <p className="text-xs text-muted-foreground">
                Si necesitás más de uno, separalos con coma. Se usan sólo para vincular este formulario; no cambian el email original ni el informe.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} disabled={isEditing}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={isEditing} data-testid="edit-submission-save">
              {isEditing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
              ) : (
                'Guardar Cambios'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <Button variant="ghost" size="sm" onClick={onBack} className="shrink-0">
                  <ArrowLeft className="w-4 h-4" />
                  Volver
                </Button>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {isFormu2Submission(submission) && (
                      <Badge
                        variant="outline"
                        className="shrink-0 border-teal-300 bg-teal-50 text-teal-800 dark:border-teal-800 dark:bg-teal-950/40 dark:text-teal-100"
                        title="Formulario con audio"
                      >
                        F2
                      </Badge>
                    )}
                    {!isFormu2Submission(submission) && isLegacyAudioEnabled(submission) && (
                      <Badge
                        variant="outline"
                        className="shrink-0 border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
                        title="Audio habilitado manualmente para formulario clásico"
                      >
                        Audio
                      </Badge>
                    )}
                    <h1 className="text-lg font-bold leading-6">{submission.user?.name}</h1>
                    {isTestSubmission(submission) && (
                      <Badge variant="outline" className="border-cyan-300 bg-cyan-50 text-cyan-800 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-100">
                        PRUEBA
                      </Badge>
                    )}
                    <Badge variant={submission.status === 'finalized' ? 'default' : 'secondary'}>
                      {submission.status === 'finalized' ? 'Finalizado' : 'En progreso'}
                    </Badge>
                    <AdminAudioStatusBadge submission={submission} />
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <span className="inline-flex min-w-0 items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{submission.user?.email}</span>
                    </span>
                    {leadPhone ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 shrink-0" />
                        {leadWhatsappUrl ? (
                          <a
                            href={leadWhatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-foreground underline-offset-4 hover:underline"
                          >
                            {leadPhone}
                          </a>
                        ) : (
                          <span className="font-medium text-foreground">{leadPhone}</span>
                        )}
                      </span>
                    ) : submission.lead_lookup?.configured ? (
                      <span>Teléfono no encontrado</span>
                    ) : null}
                    {submission.linking_emails?.length > 0 && (
                      <span className="text-xs">Seguimiento: {submission.linking_emails.join(', ')}</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="self-end lg:self-start">
                <ThemeToggle />
              </div>
            </div>

            <div className="flex flex-col gap-2 border-t pt-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <MeetingReportGenerator
                  submissionId={submissionId}
                  studentName={submission.user?.name}
                  studentEmail={submission.user?.email}
                  token={token}
                />
                {canRequestAudioForSubmission(submission) && (
                  <Button
                    variant="outline"
                    onClick={handleRequestAudio}
                    disabled={isRequestingAudio}
                    data-testid="admin-request-audio"
                  >
                    {isRequestingAudio ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Headphones className="w-4 h-4" />
                    )}
                    Solicitar audio
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={handleCopyText} data-testid="admin-copy-text">
                  {isCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  {isCopied ? 'Copiado' : 'Copiar texto'}
                </Button>
                <Button variant="outline" onClick={handleExportPDF} data-testid="admin-download-pdf">
                  <Download className="w-4 h-4" />
                  Descargar PDF
                </Button>
                <Button variant="outline" onClick={handleOpenEdit} data-testid="admin-edit-submission">
                  <Pencil className="w-4 h-4" />
                  Editar datos
                </Button>
                <Button variant="outline" onClick={handleOpenEditResponses} data-testid="admin-edit-responses">
                  <Pencil className="w-4 h-4" />
                  Editar respuestas
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="icon"
                      title="Eliminar formulario"
                      aria-label="Eliminar formulario"
                      data-testid="admin-delete-submission"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar este formulario?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta acción no se puede deshacer. Se eliminarán permanentemente todos los datos
                        del formulario de <strong>{submission.user?.name}</strong>.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        Eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {isAudioEnabledSubmission(submission) && (
          <Card className="border-cyan-200 bg-cyan-50/60 dark:bg-cyan-950/20 dark:border-cyan-900">
            <CardHeader>
              <CardTitle>
                {isTestSubmission(submission)
                  ? 'Formulario de prueba /formu2'
                  : isFormu2Submission(submission)
                    ? 'Formulario con audio de reafirmación'
                    : 'Audio de reafirmación habilitado'}
              </CardTitle>
              <CardDescription>
                {isTestSubmission(submission)
                  ? 'Esta submission viene del flujo beta anterior al pase a producción.'
                  : isFormu2Submission(submission)
                    ? 'Esta submission corresponde al flujo productivo actual con frases y audio.'
                    : 'Esta submission viene del formulario clásico y fue integrada manualmente al flujo de audio.'}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Meeting Guide */}
        <Card>
          <CardHeader>
            <CardTitle>Guia de Devolucion en Vivo</CardTitle>
            <CardDescription>
              {submission.status === 'finalized'
                ? 'Guia privada para que Dario use durante la reunion. No es el informe que recibe el alumno.'
                : 'Este bloque se completa cuando el alumno finaliza el formulario.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submission.status === 'finalized' ? (
              <>
                <div className="flex justify-between gap-2">
                  <div className="text-xs text-muted-foreground">
                    {submission.meeting_guide_generated_at
                      ? `Generada: ${new Date(submission.meeting_guide_generated_at).toLocaleString('es-AR')}`
                      : 'Todavia no se genero la guia para esta reunion.'}
                  </div>
                  <div className="flex gap-2">
                    {hasMeetingGuide && (
                      <Button variant="outline" size="sm" onClick={handleCopyLiveGuide}>
                        {isGuideCopied ? <Check className="w-4 h-4 mr-2 text-green-500" /> : <Copy className="w-4 h-4 mr-2" />}
                        {isGuideCopied ? 'Copiado!' : 'Copiar Guia'}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateMeetingGuide}
                      disabled={isGeneratingGuide}
                    >
                      {isGeneratingGuide ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando...</>
                      ) : (
                        <>{hasMeetingGuide ? 'Regenerar Guia' : 'Generar Guia'}</>
                      )}
                    </Button>
                  </div>
                </div>

                {hasMeetingGuide ? (
                  <Textarea
                    value={submission.meeting_guide}
                    readOnly
                    className="min-h-[420px] text-sm leading-6"
                    data-testid="admin-meeting-guide"
                  />
                ) : (
                  <div className="rounded-lg border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
                    Genera la guia para ver el mini informe de devolucion que Dario puede leer durante la reunion.
                  </div>
                )}
              </>
            ) : (
              <div className="rounded-lg border border-dashed bg-muted/20 p-4 text-sm text-muted-foreground">
                Este bloque se completa cuando el alumno finaliza el formulario.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin Notes Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-yellow-500" />
              Notas del Admin
            </CardTitle>
            <CardDescription>
              Notas privadas sobre este alumno (solo visible para admins)
              {notesLastSaved && (
                <span className="ml-2 text-xs">
                  — Guardado: {notesLastSaved.toLocaleString('es-AR')}
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Escribir notas sobre este alumno..."
                rows={4}
                className="resize-y"
              />
              <div className="flex justify-end">
                <Button onClick={handleSaveNotes} disabled={isSavingNotes} size="sm">
                  {isSavingNotes ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Guardar Notas</>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* AI Summary if exists */}
        {submission.ai_summary && (
          <Card>
            <CardHeader>
              <CardTitle>Resumen IA</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="whitespace-pre-wrap text-sm">{submission.ai_summary}</div>
            </CardContent>
          </Card>
        )}

        {/* PDF Content */}
        <div id="submission-pdf-content" className="space-y-6 bg-card p-6 rounded-lg shadow-sm border">
          <div className="text-center border-b pb-4">
            <h1 className="text-2xl font-bold text-primary">Academia Cortex</h1>
            <h2 className="text-xl font-semibold mt-2">Planificador de Vida</h2>
            <p className="text-muted-foreground mt-1">{submission.user?.name} - {submission.user?.email}</p>
            <p className="text-sm text-muted-foreground">
              Creado: {new Date(submission.created_at).toLocaleDateString('es-AR')}
              {submission.finalized_at && ` | Finalizado: ${new Date(submission.finalized_at).toLocaleDateString('es-AR')}`}
            </p>
          </div>

          <Separator />

          {/* Life Squares */}
          {submission.life_squares?.currentAge && submission.life_squares?.targetAge && (
            <>
              <div>
                <h3 className="text-lg font-semibold mb-4">Cuadraditos de Vida</h3>
                <LifeSquares
                  currentAge={submission.life_squares.currentAge}
                  targetAge={submission.life_squares.targetAge}
                  name={submission.user?.name}
                />
              </div>
              <Separator />
            </>
          )}

          {/* Wheel */}
          {submission.wheel_data && Object.keys(submission.wheel_data).length > 0 && (
            <>
              <div>
                <h3 className="text-lg font-semibold mb-4">Rueda de la Vida</h3>
                <div className="grid lg:grid-cols-2 gap-4">
                  <WheelChart labels={labels} values={values} />
                  <div className="space-y-2">
                    {wheelCategories.map((cat) => {
                      const score = submission.wheel_data[cat.id]?.score || 0;
                      return (
                        <div
                          key={cat.id}
                          className="flex items-center justify-between p-2 rounded"
                          style={{ backgroundColor: `${cat.color}20` }}
                        >
                          <span className="font-medium">{cat.name}</span>
                          <span className="font-bold text-primary">{score.toFixed(1)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Objectives */}
          {submission.objectives && (
            <>
              <div>
                <h3 className="text-lg font-semibold mb-4">Objetivos</h3>
                <div className="space-y-6">
                  {['3m', '6m', '9m'].map((period) => {
                    const periodObjectives = submission.objectives[period] || [];
                    const title = period === '3m' ? '3 Meses' : period === '6m' ? '6 Meses' : '9 Meses';
                    
                    if (!periodObjectives.some(obj => obj.description)) return null;

                    return (
                      <Card key={period}>
                        <CardHeader className="py-3 bg-muted/50">
                          <CardTitle className="text-base">{title}</CardTitle>
                        </CardHeader>
                        <CardContent className="py-4">
                          {periodObjectives.map((obj, idx) => {
                            if (!obj.description) return null;
                            return (
                              <div key={idx} className="mb-4 last:mb-0">
                                <div className="flex items-start justify-between mb-2">
                                  <strong className="text-primary">{idx + 1}. {obj.description}</strong>
                                  {obj.dailyTime && (
                                    <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded">
                                      {obj.dailyTime}/día
                                    </span>
                                  )}
                                </div>
                                {obj.actions?.filter(a => a).length > 0 && (
                                  <div className="ml-4">
                                    <p className="text-sm font-medium text-muted-foreground mb-1">Acciones:</p>
                                    <ul className="list-disc list-inside text-sm space-y-1">
                                      {obj.actions.filter(a => a).map((action, i) => (
                                        <li key={i}>{action}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Question Answers */}
          {submission.question_answers && submission.question_answers.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Respuestas a Preguntas</h3>
              <div className="space-y-3">
                {submission.question_answers.map((qa, idx) => {
                  const answer = qa.answer;
                  if (!answer || (Array.isArray(answer) && answer.length === 0)) return null;
                  const questionText = getQuestionText(qa.question_id, qa);
                  const displayAnswer = Array.isArray(answer) ? answer.join(', ') : answer;
                  return (
                    <div key={idx} className="p-3 bg-muted/30 rounded-lg">
                      <p className="font-medium text-sm">{questionText}</p>
                      <p className="text-sm mt-1 text-muted-foreground">{displayAnswer}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* AI Summary in PDF */}
          {submission.ai_summary && (
            <>
              <Separator />
              <div>
                <h3 className="text-lg font-semibold mb-4">Resumen y Recomendaciones IA</h3>
                <div className="whitespace-pre-wrap text-sm bg-muted/30 p-4 rounded-lg">
                  {submission.ai_summary}
                </div>
              </div>
            </>
          )}
        </div>


        {/* Edit Responses Dialog */}
        <Dialog open={isEditResponsesOpen} onOpenChange={setIsEditResponsesOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Editar Respuestas de {submission.user?.name}</DialogTitle>
              <DialogDescription>
                Modificar las respuestas del formulario. Los cambios se guardan al presionar "Guardar Todo".
              </DialogDescription>
            </DialogHeader>
            {editResponses && (
              <div className="flex-1 pr-4 overflow-y-auto" style={{ maxHeight: '70vh' }}>
                <div className="space-y-6 pb-4">
                  {/* Life Squares */}
                  {editResponses.life_squares && (
                    <div>
                      <h4 className="font-semibold text-sm mb-3 text-primary">Cuadraditos de Vida</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Edad Actual</Label>
                          <Input
                            type="number"
                            value={editResponses.life_squares.currentAge || ''}
                            onChange={(e) => updateLifeSquares('currentAge', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Expectativa de Vida</Label>
                          <Input
                            type="number"
                            value={editResponses.life_squares.targetAge || ''}
                            onChange={(e) => updateLifeSquares('targetAge', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Wheel Data */}
                  {editResponses.wheel_data && Object.keys(editResponses.wheel_data).length > 0 && (
                    <div>
                      <h4 className="font-semibold text-sm mb-3 text-primary">Rueda de la Vida (Puntajes)</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {wheelCategories.map(cat => {
                          const score = editResponses.wheel_data[cat.id]?.score || 0;
                          return (
                            <div key={cat.id} className="flex items-center gap-2">
                              <span className="text-sm w-32 truncate" title={cat.name}>{cat.name}</span>
                              <Input
                                type="number"
                                min="0"
                                max="10"
                                step="0.1"
                                value={score}
                                onChange={(e) => updateWheelScore(cat.id, e.target.value)}
                                className="w-20"
                              />
                              <span className="text-xs text-muted-foreground">/10</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Question Answers */}
                  {editResponses.question_answers && editResponses.question_answers.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-sm mb-3 text-primary">Respuestas a Preguntas</h4>
                      <div className="space-y-4">
                        {editResponses.question_answers.map((qa, idx) => {
                          const questionText = getQuestionText(qa.question_id, qa);
                          const answer = qa.answer;
                          const isArray = Array.isArray(answer);
                          return (
                            <div key={idx} className="space-y-1">
                              <Label className="text-xs font-medium">{questionText}</Label>
                              {isArray ? (
                                <Input
                                  value={answer.join(', ')}
                                  onChange={(e) => updateQuestionAnswer(idx, e.target.value.split(', '))}
                                />
                              ) : (
                                <Textarea
                                  value={answer || ''}
                                  onChange={(e) => updateQuestionAnswer(idx, e.target.value)}
                                  rows={2}
                                  className="resize-y text-sm"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Objectives */}
                  {editResponses.objectives && (
                    <div>
                      <h4 className="font-semibold text-sm mb-3 text-primary">Objetivos</h4>
                      {['3m', '6m', '9m'].map(period => {
                        const periodObjs = editResponses.objectives[period] || [];
                        const title = period === '3m' ? '3 Meses' : period === '6m' ? '6 Meses' : '9 Meses';
                        if (periodObjs.length === 0) return null;
                        return (
                          <div key={period} className="mb-4">
                            <h5 className="text-xs font-semibold mb-2 text-muted-foreground">{title}</h5>
                            {periodObjs.map((obj, idx) => (
                              <div key={idx} className="mb-3 p-3 border rounded-lg space-y-2">
                                <div>
                                  <Label className="text-xs">Objetivo {idx + 1}</Label>
                                  <Input
                                    value={obj.description || ''}
                                    onChange={(e) => updateObjective(period, idx, 'description', e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">Tiempo diario</Label>
                                  <Input
                                    value={obj.dailyTime || ''}
                                    onChange={(e) => updateObjective(period, idx, 'dailyTime', e.target.value)}
                                  />
                                </div>
                                {obj.actions && (
                                  <div>
                                    <Label className="text-xs">Acciones (una por linea)</Label>
                                    <Textarea
                                      value={(obj.actions || []).join('\n')}
                                      onChange={(e) => updateObjective(period, idx, 'actions', e.target.value.split('\n'))}
                                      rows={2}
                                      className="resize-y text-sm"
                                    />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIsEditResponsesOpen(false)} disabled={isSavingResponses}>
                Cancelar
              </Button>
              <Button onClick={handleSaveResponses} disabled={isSavingResponses}>
                {isSavingResponses ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> Guardar Todo</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </main>
    </div>
  );
}

export default AdminSubmissionView;
